/* SSi website – per-language landing page route.
 *
 * Single dynamic route (`/learn/:lang`) that picks composition + tweak
 * defaults based on the slug. Per-language Hero / Vignettes / closer
 * components live in `../components/lang/`; shared sections (Methodology,
 * Outcomes, TestimonialFeature, SampleAndPricing, FAQ, Footer) come from
 * the existing site-wide imports.
 *
 * Composition per slug mirrors what the static `learn-<lang>.html`
 * pages render. Welsh has its own Reasons + Closers extras; the other
 * nine languages share the standard Hero → Vignettes shape.
 */
import { useParams } from 'react-router-dom';
import { useTweaks } from '../components/Tweaks.jsx';
import { useOffer, useOfferTweakDefaults } from '../offers/OfferContext.jsx';
import { Header } from '../components/Header.jsx';
import { Methodology } from '../components/Methodology.jsx';
import { IntensiveCrossLink } from '../components/IntensiveCrossLink.jsx';
import { Outcomes } from '../components/Outcomes.jsx';
import { TestimonialFeature } from '../components/Testimonials.jsx';
import { SampleAndPricing } from '../components/Sample.jsx';
import { FAQ } from '../components/FAQ.jsx';
import { Footer } from '../components/Blocks.jsx';
import NotFound from './NotFound.jsx';

import { SpanishHero } from '../components/lang/SpanishHero.jsx';
import { SpanishVignettes } from '../components/lang/SpanishVignettes.jsx';
import { FrenchHero } from '../components/lang/FrenchHero.jsx';
import { FrenchVignettes } from '../components/lang/FrenchVignettes.jsx';
import { GermanHero } from '../components/lang/GermanHero.jsx';
import { GermanVignettes } from '../components/lang/GermanVignettes.jsx';
import { ItalianHero } from '../components/lang/ItalianHero.jsx';
import { ItalianVignettes } from '../components/lang/ItalianVignettes.jsx';
import { PortugueseHero } from '../components/lang/PortugueseHero.jsx';
import { PortugueseVignettes } from '../components/lang/PortugueseVignettes.jsx';
import { MandarinHero } from '../components/lang/MandarinHero.jsx';
import { MandarinVignettes } from '../components/lang/MandarinVignettes.jsx';
import { JapaneseHero } from '../components/lang/JapaneseHero.jsx';
import { JapaneseVignettes } from '../components/lang/JapaneseVignettes.jsx';
import { KoreanHero } from '../components/lang/KoreanHero.jsx';
import { KoreanVignettes } from '../components/lang/KoreanVignettes.jsx';
import { ArabicHero } from '../components/lang/ArabicHero.jsx';
import { ArabicVignettes } from '../components/lang/ArabicVignettes.jsx';
import { WelshHero } from '../components/lang/WelshHero.jsx';
import { WelshReasons } from '../components/lang/WelshReasons.jsx';
import { WelshVignettes } from '../components/lang/WelshVignettes.jsx';
// WelshClosers exports three separate sections; pull them all by name.
import { WelshSomethingBigger, WelshClosing, WelshTrialFlow } from '../components/lang/WelshClosers.jsx';

/* ───── shared tweak defaults ─────
 * Most copy outside the Hero / Vignettes blocks is identical across the
 * non-Welsh language pages — the static HTMLs duplicate it verbatim. We
 * keep one base object here and let per-language TWEAK_DEFAULTS shallow-
 * spread their overrides on top.
 */
const SHARED_DEFAULTS = {
  navWelshResources: 'Welsh Resources',
  navCommunity: 'Community',
  navAboutUs: 'About us',
  navBlog: 'Blog',
  navContact: 'Contact',
  login: 'Login',
  startLearning: 'Start Learning',
  methKicker: 'Our methodology',
  methTitle: 'The SaySomethingin Method',
  methSub: 'This learning approach is built around four simple principles that your brain already uses to pick up language naturally.',
  methMemoriesTitle: 'Memories',
  methMemoriesBody: "Our method is based on forcing your brain to create and retain memories: for words, and (uniquely) for the 'edges' between words. It's the combination of words and edges that makes you feel like (and sound like!) a natural speaker.",
  methEdgesTitle: 'Edges',
  methEdgesBody: "'Edges' are the spaces between words that you don't normally even notice. They have vital information for you, which most courses skip over entirely. When we have (subconsciously) taught you enough 'edges', you will speak correctly because it 'just sounds right'.",
  methMistakesTitle: 'Mistakes',
  methMistakesBody: "Mistakes are part of the method, not something to avoid. When you notice a 'mistake', you're noticing the difference between what you said and what you heard - and that is exactly how the brain learns. Every single mistake is a golden moment of learning.",
  methConfidenceTitle: 'Confidence',
  methConfidenceBody: "When you're happy making mistakes, you get exposed more quickly to more words and more edges - and that is what builds the unusual confidence that SaySomethingin learners are renowned for. And confidence builds fluency.",
  testiKicker: 'A word from Ruth Jones',
  testiQuote: "It's absolutely brilliant, it's really good fun, it's really easy to use - I don't know how it works but it does. I can't recommend it highly enough.",
  testiName: 'Ruth Jones',
  testiRole: 'Actor & writer (Gavin & Stacey)',
  testiCredit: 'As seen on BBC Radio 4',
  testiMoreLink: 'Watch more learner stories →',
  outcomesKicker: 'What to expect',
  outcomesTitle: 'Fast, OR easy. Your choice.',
  outcomesLede: "Other language courses often claim to be fast AND easy. But there's no such thing. Fast requires effort. Easy requires time. You can take either path with us, but you need to know that it's a choice.",
  outcomesFastTitle: 'Fast.',
  outcomesFastTrade: 'Costs: effort.',
  outcomesFastBody: "When you've got a deadline – a filming day, a keynote, a trip – the only thing that gets you there is effort. Here are two ways to make that effort:",
  outcomesFastA_Label: 'Self-driven',
  outcomesFastA_Title: 'Crash-test yourself in-app.',
  outcomesFastA_Body: "Stay in the course as long as you like. We've tested learners (and ourselves!) doing up to ten intensive days in a row – and you come out the other side talking. No daily cap, no limits, and no rest.",
  outcomesFastA_Stat: 'Go as hard as you like for as long as you like',
  outcomesFastA_StatLabel: 'We love learning quickly. More than 5 days gets a bit brain melting, but gives you startling results. Even 1 intensive day per month will give you remarkably fast learning.',
  outcomesFastB_Label: 'In-person with the SaySomethingin Method author',
  outcomesFastB_Title: '1:1 coaching, in the room.',
  outcomesFastB_Body: 'For TV productions, sports teams, and business leaders preparing for a high-stakes conversation. Our methodology author Aran works with you directly – intensive, tailored, and confidential.',
  outcomesFastB_Stat: "Give us 5 days, we'll give you a new language",
  outcomesFastB_StatLabel: "This is what our TV learners pull off consistently. By application, limited slots. You won't be fluent after 5 days, but you will have had your first conversations, and you'll amaze yourself. ",
  outcomesEasyTitle: 'Easy.',
  outcomesEasyTrade: 'Costs: time.',
  outcomesEasyBody: 'Five minutes a day. No streaks to protect, no guilt, no cramming. Just consistent, spoken practice that builds into real conversations.',
  outcomesEasyStat1: '1–3 months.',
  outcomesEasyStat2: '3–6 months.',
  outcomesEasyStat: '6–12 months.',
  outcomesEasyMeta: 'The SaySomethingin Method – for everyone',
  outcomesFooter: "What we won't promise: fluency in 10 minutes a day in 3 months. Fast AND easy isn't a thing.",
  sampleKicker: 'Hear it yourself',
  sampleTitle: "Don't just take their word for it.",
  faqKicker: 'Questions',
  faqTitle: 'The things people often ask before signing up.',
  faqLede: "Straight answers. If there isn't an answer to what's on your mind here, drop us a line for a personal response.",
  faqContact: 'Email the team →',
  footerCta1: 'Start Learning',
  footerCta2: 'Get our newsletter',
  footerCol1: 'What we do',
  footerCol2: 'SaySomethingin Learners',
  footerCol3: 'Download',
  footerLegal: '© 2026 SaySomethingIn Cyf. Glaslyn, Ffordd y Parc, Bangor, Gwynedd LL57 4FE, Wales.',
};

// Build a per-language Easy-milestones / FAQ template that mentions the
// language by name. The static HTMLs do this verbatim across nine of the
// non-Welsh pages, so codifying the template here keeps the per-slug
// objects small and prevents drift.
function langEasyAndFaq(name) {
  return {
    outcomesEasyStatLabel1: `The first ${name} words stop feeling foreign. You're building sentences out loud – clumsy, slow, but real sentences – and you might have your first experience of ${name} 'just bubbling up in your brain', as some of our learners say.`,
    outcomesEasyStatLabel2: `You can hold a simple conversation. It's still full of gaps, you're still hunting for words, but you're talking in ${name} instead of about it. Most learners stall here; you won't, because the daily five minutes keeps moving you forwards.`,
    outcomesEasyStatLabel: `Your spoken ${name} is beginning to transform. Real conversations, in real time, with real people. Over a hundred thousand people have used this approach. You can too.`,
    sampleLede: `Try three minutes – or half an hour! – of the SaySomethingin Method, right here. ${name} is teed up; you can hop into any of the others too.`,
    learnerSub: `You just heard the method. Here's what happened to ordinary people who stuck with it – from all over the world, speaking ${name} with SaySomethingin.`,
    faqQ1: `I've tried language apps before and never got to actually speaking ${name}. Why is this different?`,
    faqA1: `Because we don't let you 'recognise' your way through a course. From the very first lesson you're producing ${name} sentences out loud, with a pause that forces you to commit before you hear the answer. That's where speaking ability actually comes from – and it's the thing most apps quietly skip.`,
    faqQ2: `I'm older / I don't have a 'language brain' / school ${name} put me off. Will it still work?`,
    faqA2: `Yes. Our oldest active learner is in her eighties. Plenty of our ${name} learners come back to it after decades of "I'm no good at languages", and most of them end up having their first real conversation within a few months. The method doesn't require talent – it requires showing up.`,
    faqQ3: 'How much time, honestly – will five minutes a day actually get me anywhere?',
    faqA3: `Yes, if you do it most days. Five minutes of spoken practice a day will transform your spoken ${name} in 6–12 months. Ten minutes a day gets you there faster. Cramming three hours on Sunday and nothing else works as well, if that is a better fit for you.`,
    faqQ4: 'Which variety of the language will I learn?',
    faqA4: `We focus on the most widely understood form of ${name}, with regional notes where they matter. More variants coming soon.`,
    faqQ5: 'Do I actually have to say it out loud? I listen on the train / with family around.',
    faqA5: "Yes, out loud is the course. Whispering works. Mumbling works. Silently mouthing the words works better than nothing. What doesn't work is only listening – your brain needs to try to produce the sounds for them to stick. ",
    faqQ6: 'What happens after I finish the course – will I actually be able to have a conversation?',
    faqA6: `Yes, you will. And the more conversations you have, the easier they will feel, and the more your ${name} will flow. `,
    faqQ7: "What does it cost, and what if it doesn't work for me?",
    faqA7: "It's £15 per month (or £150 per year if you'd like to save £30). And you can cancel any time.",
    footerCta: `Start speaking ${name} today.`,
  };
}

/* ───── per-language defaults (Hero + Vignettes only; other keys come from SHARED_DEFAULTS + langEasyAndFaq) ───── */

const SPANISH_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Spanish'),
  esHeroVariant: 'A',
  esHeroKicker: '',
  esHeroTitle: 'High intensity speaking exercises for Spanish',
  esHeroSub1: 'Your Spanish becomes joyful when you start having actual conversations with other people.',
  esHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  esHeroCta1: 'Start your 7 days free',
  esHeroCta2: 'Hear a sample first',
  esHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  esHeroGiantWord: 'Hablar.',
  esVigKicker: "Where you'll use it",
  esVigTitle: 'What Spanish actually unlocks.',
  esVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  esVigTag1: 'A bar in Seville',
  esVigTitle1: 'Order like a local.',
  esVigBody1: "The place with no English menu. The waiter who's seen a thousand tourists point. You're not one of them today.",
  esVigPhrase1: 'Para mí, las bravas y una caña, por favor.',
  esVigPhraseEn1: 'Patatas bravas and a small beer, please.',
  esVigTag2: 'A video call',
  esVigTitle2: 'Talk to the in-laws.',
  esVigBody2: "Your partner's abuela in Valencia. She's always been kind about your silence. Now you get to hear her stories first-hand.",
  esVigPhrase2: 'Cuéntame, abuela. ¿Cómo era el pueblo antes?',
  esVigPhraseEn2: 'Tell me, Grandma. What was the village like before?',
  esVigTag3: 'A Saturday market',
  esVigTitle3: 'Haggle without hand-signs.',
  esVigBody3: 'The Oaxaca market, the Madrid rastro, the Barcelona boqueria. Real prices, proper banter, a better tomato than you\'ve ever bought.',
  esVigPhrase3: '¿Me hace un descuento si llevo dos?',
  esVigPhraseEn3: 'Would you give me a discount if I take two?',
  esVigTag4: 'The Camino',
  esVigTitle4: 'Make friends on the trail.',
  esVigBody4: "Pilgrims from eight countries, one shared language at dinner. The walk's better when you're part of the table.",
  esVigPhrase4: '¿De dónde venís? Yo empecé en Sarria.',
  esVigPhraseEn4: 'Where are you coming from? I started in Sarria.',
};

const FRENCH_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('French'),
  frHeroVariant: 'A',
  frHeroKicker: '',
  frHeroTitle: 'High intensity speaking exercises for French',
  frHeroSub1: 'Your French becomes joyful when you start having actual conversations with other people.',
  frHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  frHeroCta1: 'Start your 7 days free',
  frHeroCta2: 'Hear a sample first',
  frHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  frHeroGiantWord: 'Parler.',
  frHeroBubble: 'Bonjour !',
  frVigKicker: "Where you'll use it",
  frVigTitle: 'What French actually unlocks.',
  frVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  frVigTag1: 'A bistro in Lyon',
  frVigTitle1: 'Order without pointing.',
  frVigBody1: "The chalkboard menu, the impatient queue behind you. The waiter has already decided what you'll have. You change his mind.",
  frVigPhrase1: "Je prendrai le plat du jour, et un verre de rouge, s'il vous plaît.",
  frVigPhraseEn1: "I'll have today's special, and a glass of red, please.",
  frVigTag2: 'A wedding in Provence',
  frVigTitle2: 'Hold up your end of dinner.',
  frVigBody2: "Your partner's cousins, three generations around one long table. Until now you've been the silent one. Tonight you're not.",
  frVigPhrase2: 'À la vôtre ! Quelle belle fête, merci de nous accueillir.',
  frVigPhraseEn2: 'Cheers! What a beautiful party, thank you for having us.',
  frVigTag3: 'A market in Sarlat',
  frVigTitle3: 'Talk cheese, properly.',
  frVigBody3: "The fromager who quizzes everyone. The wheel that's been ageing since spring. You ask for a taste — and you understand the answer.",
  frVigPhrase3: 'Lequel est le plus corsé ? Je le veux pour ce soir.',
  frVigPhraseEn3: 'Which is the strongest? I want it for tonight.',
  frVigTag4: 'A taxi in Paris',
  frVigTitle4: 'Make small talk that lands.',
  frVigBody4: "Twenty minutes through the rain to Gare du Nord. The driver asks where you're from. The whole ride changes.",
  frVigPhrase4: "C'est ma première fois à Paris en hiver — il pleut toujours autant ?",
  frVigPhraseEn4: "It's my first time in Paris in winter — does it always rain this much?",
};

const GERMAN_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('German'),
  deHeroVariant: 'A',
  deHeroKicker: '',
  deHeroTitle: 'High intensity speaking exercises for German',
  deHeroSub1: 'Your German becomes joyful when you start having actual conversations with other people.',
  deHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  deHeroCta1: 'Start your 7 days free',
  deHeroCta2: 'Hear a sample first',
  deHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  deHeroGiantWord: 'Sprechen.',
  deHeroBubble: 'Hallo!',
  deVigKicker: "Where you'll use it",
  deVigTitle: 'What German actually unlocks.',
  deVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  deVigTag1: 'A Berlin Kneipe',
  deVigTitle1: 'Order a round, properly.',
  deVigBody1: "The corner pub at 11pm, the regulars who've clocked you. You walk to the bar, you know exactly what you want and exactly what to say.",
  deVigPhrase1: 'Drei Pils, bitte — und ein Wasser dazu.',
  deVigPhraseEn1: 'Three pilsners, please — and a water with them.',
  deVigTag2: 'A Munich Christmas market',
  deVigTitle2: 'Buy gifts, share stories.',
  deVigBody2: "The wooden stalls, the Glühwein, the maker who carved the figure himself. You don't just point at the price tag.",
  deVigPhrase2: "Wo haben Sie das gemacht? Ich nehme zwei davon.",
  deVigPhraseEn2: "Where did you make this? I'll take two of them.",
  deVigTag3: 'A trail in the Alps',
  deVigTitle3: 'Chat with strangers on the path.',
  deVigBody3: 'Hut to hut, mostly Germans and Austrians, mostly retired, all faster than you. Lunch is when you catch up.',
  deVigPhrase3: 'Sind Sie schon mal die ganze Tour gelaufen?',
  deVigPhraseEn3: 'Have you done the whole route before?',
  deVigTag4: 'A meeting in Hamburg',
  deVigTitle4: 'Switch out of the small-talk script.',
  deVigBody4: "The first ten minutes before the real meeting starts. The colleagues who've only ever heard you in English. Until today.",
  deVigPhrase4: 'Wie war Ihr Wochenende? Bei uns hat es endlos geregnet.',
  deVigPhraseEn4: 'How was your weekend? It rained endlessly here.',
};

const ITALIAN_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Italian'),
  itHeroVariant: 'A',
  itHeroKicker: '',
  itHeroTitle: 'High intensity speaking exercises for Italian',
  itHeroSub1: 'Your Italian becomes joyful when you start having actual conversations with other people.',
  itHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  itHeroCta1: 'Start your 7 days free',
  itHeroCta2: 'Hear a sample first',
  itHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  itHeroGiantWord: 'Parlare.',
  itHeroBubble: 'Ciao!',
  itVigKicker: "Where you'll use it",
  itVigTitle: 'What Italian actually unlocks.',
  itVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  itVigTag1: 'A trattoria in Rome',
  itVigTitle1: 'Order what the locals order.',
  itVigBody1: 'The handwritten menu, the nonna in the kitchen, the waiter who calls everyone amore. You skip the tourist pasta.',
  itVigPhrase1: 'Per me la cacio e pepe, e un quarto di vino della casa.',
  itVigPhraseEn1: "I'll have the cacio e pepe, and a quarter of house wine.",
  itVigTag2: 'A coffee in Naples',
  itVigTitle2: 'Stand at the bar like you mean it.',
  itVigBody2: 'Thirty seconds, one espresso, two euros, gone. You order, drink, pay, leave. No fumbling.',
  itVigPhrase2: 'Un caffè e un cornetto, grazie. Lo prendo al banco.',
  itVigPhraseEn2: "A coffee and a croissant, please. I'll have it at the bar.",
  itVigTag3: 'A village in Sicily',
  itVigTitle3: 'Get the real recommendation.',
  itVigBody3: "Not the TripAdvisor pin. The cousin's cousin who has the boat. You ask, and you get.",
  itVigPhrase3: 'Mi consiglia un posto che non sia per i turisti?',
  itVigPhraseEn3: "Can you recommend somewhere that's not for tourists?",
  itVigTag4: 'A train to Florence',
  itVigTitle4: 'Make a friend in two stops.',
  itVigBody4: "The retired teacher next to you with a book and an opinion. By Bologna you're both laughing.",
  itVigPhrase4: 'Cosa sta leggendo? Sembra interessante.',
  itVigPhraseEn4: 'What are you reading? It looks interesting.',
};

const PORTUGUESE_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Portuguese'),
  ptHeroVariant: 'A',
  ptHeroKicker: '',
  ptHeroTitle: 'High intensity speaking exercises for Portuguese',
  ptHeroSub1: 'Your Portuguese becomes joyful when you start having actual conversations with other people.',
  ptHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  ptHeroCta1: 'Start your 7 days free',
  ptHeroCta2: 'Hear a sample first',
  ptHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  ptHeroGiantWord: 'Falar.',
  ptHeroBubble: 'Olá!',
  ptVigKicker: "Where you'll use it",
  ptVigTitle: 'What Portuguese actually unlocks.',
  ptVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  ptVigTag1: 'A tasca in Lisbon',
  ptVigTitle1: 'Eat where the cab drivers eat.',
  ptVigBody1: "No menu in English, no tablecloth, no nonsense. The lunch special is whatever's good today. You ask, and you find out.",
  ptVigPhrase1: 'O que recomenda hoje? Eu confio em si.',
  ptVigPhraseEn1: 'What do you recommend today? I trust you.',
  ptVigTag2: 'A beach bar in Porto',
  ptVigTitle2: 'Order a round at sunset.',
  ptVigBody2: 'The football match on the screen, the regulars at the next table, the sea behind them. You join in.',
  ptVigPhrase2: 'Mais duas imperiais, se faz favor — e o jogo, como vai?',
  ptVigPhraseEn2: 'Two more lagers, please — and how is the match going?',
  ptVigTag3: 'A favela tour in Rio',
  ptVigTitle3: 'Listen to the people who live there.',
  ptVigBody3: "Not the postcard version. The artist, the grandmother, the kid with the kite. You don't need a translator.",
  ptVigPhrase3: 'Como é que era aqui quando você era criança?',
  ptVigPhraseEn3: 'What was it like here when you were a child?',
  ptVigTag4: 'A pousada in the Algarve',
  ptVigTitle4: 'Become a regular in three days.',
  ptVigBody4: 'The owner who brings you fresh oranges. The neighbour who knocks. By Friday they know your name.',
  ptVigPhrase4: 'Bom dia! As laranjas hoje estão maravilhosas, obrigado.',
  ptVigPhraseEn4: 'Good morning! The oranges are wonderful today, thank you.',
};

const MANDARIN_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Mandarin'),
  zhHeroVariant: 'A',
  zhHeroKicker: '',
  zhHeroTitle: 'High intensity speaking exercises for Mandarin',
  zhHeroSub1: 'Your Mandarin becomes joyful when you start having actual conversations with other people.',
  zhHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  zhHeroCta1: 'Start your 7 days free',
  zhHeroCta2: 'Hear a sample first',
  zhHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  zhHeroGiantWord: '说话.',
  zhHeroBubble: '你好!',
  zhVigKicker: "Where you'll use it",
  zhVigTitle: 'What Mandarin actually unlocks.',
  zhVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  zhVigTag1: 'A noodle bar in Shanghai',
  zhVigTitle1: 'Order without the picture menu.',
  zhVigBody1: "The plastic stools, the steamed buns at the next table, the cook who is also the cashier. You don't hold up the queue.",
  zhVigPhrase1: '老板，来一碗牛肉面，不要香菜。',
  zhVigPhraseEn1: 'Boss, one beef noodle soup, no coriander.',
  zhVigTag2: 'A taxi in Beijing',
  zhVigTitle2: 'Get there without the address app.',
  zhVigBody2: 'The driver who only knows landmarks. The traffic on the Second Ring. You explain, he nods, you arrive.',
  zhVigPhrase2: '麻烦您，到鼓楼大街，不走二环。',
  zhVigPhraseEn2: 'Please take me to Gulou Street, but not via the Second Ring.',
  zhVigTag3: 'A market in Chengdu',
  zhVigTitle3: 'Talk spice with the seller.',
  zhVigBody3: 'The dried chillies, the Sichuan peppercorns, the sceptical look at the foreigner. You ask which is hottest. They smile.',
  zhVigPhrase3: '哪一种最辣？我能吃辣，真的。',
  zhVigPhraseEn3: 'Which is the hottest? I can handle spice, really.',
  zhVigTag4: 'A WeChat call',
  zhVigTitle4: "Talk to your kids' grandparents.",
  zhVigBody4: "The in-laws back home, the screen they're too close to. They've been kind about your silence. Now you join in.",
  zhVigPhrase4: '爷爷奶奶好！孩子今天学了一首新歌。',
  zhVigPhraseEn4: 'Hello Grandpa, Grandma! The children learned a new song today.',
};

const JAPANESE_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Japanese'),
  jaHeroVariant: 'A',
  jaHeroKicker: '',
  jaHeroTitle: 'High intensity speaking exercises for Japanese',
  jaHeroSub1: 'Your Japanese becomes joyful when you start having actual conversations with other people.',
  jaHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  jaHeroCta1: 'Start your 7 days free',
  jaHeroCta2: 'Hear a sample first',
  jaHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  jaHeroGiantWord: '話す.',
  jaHeroBubble: 'こんにちは!',
  jaVigKicker: "Where you'll use it",
  jaVigTitle: 'What Japanese actually unlocks.',
  jaVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  jaVigTag1: 'An izakaya in Tokyo',
  jaVigTitle1: 'Order like a regular.',
  jaVigBody1: 'The sliding door, the row of salarymen at the counter, the chef who looks up once. You take a seat. You speak first.',
  jaVigPhrase1: 'すみません、生ビールと、おすすめを一品お願いします。',
  jaVigPhraseEn1: 'Excuse me, a draft beer and one of your recommendations, please.',
  jaVigTag2: 'A ryokan in Hakone',
  jaVigTitle2: 'Get the bath etiquette right.',
  jaVigBody2: 'The hot spring, the careful rules, the okami-san who explains. You understand. You bow. You belong, briefly.',
  jaVigPhrase2: '夕食は何時からですか。お風呂を先にいただいてもいいですか。',
  jaVigPhraseEn2: 'What time is dinner? May I take the bath first?',
  jaVigTag3: 'A shrine in Kyoto',
  jaVigTitle3: 'Make small talk with a stranger.',
  jaVigBody3: "The retired man feeding the koi, the camera around your neck, the slow afternoon. He asks where you're from. You answer in his language.",
  jaVigPhrase3: 'いい天気ですね。この池の鲤は何年ぐらいですか。',
  jaVigPhraseEn3: "Nice weather, isn't it. How old are the carp in this pond?",
  jaVigTag4: 'A sushi counter',
  jaVigTitle4: 'Talk to the chef.',
  jaVigBody4: "Eight seats, no menu, the master in front of you. You're not just a tourist tonight. You're someone he can actually talk to.",
  jaVigPhrase4: '今日のおすすめは何ですか。お任せでお願いします。',
  jaVigPhraseEn4: "What's today's recommendation? Chef's choice, please.",
};

const KOREAN_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Korean'),
  koHeroVariant: 'A',
  koHeroKicker: '',
  koHeroTitle: 'High intensity speaking exercises for Korean',
  koHeroSub1: 'Your Korean becomes joyful when you start having actual conversations with other people.',
  koHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  koHeroCta1: 'Start your 7 days free',
  koHeroCta2: 'Hear a sample first',
  koHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  koHeroGiantWord: '말하기.',
  koHeroBubble: '안녕!',
  koVigKicker: "Where you'll use it",
  koVigTitle: 'What Korean actually unlocks.',
  koVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  koVigTag1: 'A BBQ joint in Seoul',
  koVigTitle1: 'Run the table yourself.',
  koVigBody1: "The grill in the middle, the side dishes that keep coming, the question of who flips the meat. Tonight, it's you.",
  koVigPhrase1: '여기 삼겹살 2인분이랑 소주 한 병 주세요.',
  koVigPhraseEn1: 'Two portions of pork belly and a bottle of soju, please.',
  koVigTag2: 'A K-drama group chat',
  koVigTitle2: 'Keep up with the friends who watch live.',
  koVigBody2: "The episode just dropped. The thread is moving fast. You're not lurking — you're in.",
  koVigPhrase2: '이번 화 진짜 미쳐다. 마지막 장면 봤어?',
  koVigPhraseEn2: 'This episode is wild. Did you see the last scene?',
  koVigTag3: 'A jjimjilbang on a Sunday',
  koVigTitle3: 'Ask the ajumma for the right room.',
  koVigBody3: "The sauna of perfect temperature. The one with the salt floor. She'll tell you, if you ask.",
  koVigPhrase3: '혹시 소금방이 어디예요? 처음 와봐서요.',
  koVigPhraseEn3: "Where's the salt room, by chance? It's my first time here.",
  koVigTag4: 'A meeting in Pangyo',
  koVigTitle4: 'Make the first joke yourself.',
  koVigBody4: "The conference room, the colleagues who've only heard your English. You open with a line in Korean. The whole room shifts.",
  koVigPhrase4: '제가 한국어로 시작해 볼게요. 천천히 부탁드려요.',
  koVigPhraseEn4: 'Let me start in Korean. Please go easy on me.',
};

const ARABIC_DEFAULTS = {
  ...SHARED_DEFAULTS,
  ...langEasyAndFaq('Arabic'),
  arHeroVariant: 'A',
  arHeroKicker: '',
  arHeroTitle: 'High intensity speaking exercises for Arabic',
  arHeroSub1: 'Your Arabic becomes joyful when you start having actual conversations with other people.',
  arHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  arHeroCta1: 'Start your 7 days free',
  arHeroCta2: 'Hear a sample first',
  arHeroTrust: 'Over 100,000 learners · 7-day free trial · no card charged until day 8',
  arHeroGiantWord: 'تكلّم.',
  arHeroBubble: 'مرحبا!',
  arVigKicker: "Where you'll use it",
  arVigTitle: 'What Arabic actually unlocks.',
  arVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  arVigTag1: 'A souk in Marrakesh',
  arVigTitle1: 'Haggle with a smile.',
  arVigBody1: "The lanterns, the leather, the seller who's seen every line. The first price is never the price. You know that now.",
  arVigPhrase1: 'بكم هذا؟ والله غالي شوي، ممكن تنزل السعر؟',
  arVigPhraseEn1: 'How much is this? Honestly a bit pricey, can you bring the price down?',
  arVigTag2: 'A coffee in Amman',
  arVigTitle2: 'Sit, slow down, talk.',
  arVigBody2: "The cardamom in the air, the elder at the next table, the unhurried hour. You don't rush. You answer.",
  arVigPhrase2: 'القهوة عندكم لذيذة جداً. من وين أنتم؟',
  arVigPhraseEn2: 'Your coffee is delicious. Where are you from?',
  arVigTag3: 'A wedding in Cairo',
  arVigTitle3: 'Compliment the family.',
  arVigBody3: "The drums, the cousins, the bride's mother who has waited a long time for this day. You find the right words.",
  arVigPhrase3: 'مبروك! العروس جميلة جداً والفرح حلو كتير.',
  arVigPhraseEn3: 'Congratulations! The bride is beautiful and the wedding is wonderful.',
  arVigTag4: 'A taxi in Beirut',
  arVigTitle4: 'Get into a real conversation.',
  arVigBody4: "Twenty minutes, three opinions on politics, four on football, one on his mother's cooking. You hold your end.",
  arVigPhrase4: 'كيف الأحوال اليوم؟ الترافيك خانق مشان الله.',
  arVigPhraseEn4: 'How are things today? The traffic is terrible, my God.',
};

/* Welsh – distinct everywhere; do not extend langEasyAndFaq because it
 * has its own bespoke FAQ (mutations, North/South), Easy milestones,
 * Reasons cards, Closing/SomethingBigger/TrialFlow copy. */
const WELSH_DEFAULTS = {
  ...SHARED_DEFAULTS,
  cyHeroVariant: 'A',
  cyHeroKicker: '',
  cyHeroTitle: 'High intensity speaking exercises for Welsh',
  cyHeroSub1: 'Welsh becomes joyful when you start having actual conversations with other people. With your nain (okay, or your mamgu!). With the neighbours. With your kids.',
  cyHeroSub2: 'Conversations require confidence. Confidence comes from high intensity speaking: the SaySomethingin Method.',
  cyHeroCta1: 'Start your 7 days free',
  cyHeroCta2: 'Hear a sample first',
  cyHeroTrust: 'Tens of thousands of Welsh speakers have started here · 7-day free trial · no card charged until day 8',
  cyHeroGiantWord: 'Siarad.',
  cyHeroBubble: 'Shwmae!',
  cyReasonsKicker: 'Why people learn with us',
  cyReasonsTitle: "You'll recognise yourself in one of these.",
  cyReasonsLede: 'Welsh learners come to us for a lot of different reasons, but most of our learners are one of these three types.',
  cyReasonsTag1: 'Reclaiming',
  cyReasonsTitle1: 'The language was in your family.',
  cyReasonsBody1: "Maybe your grandparents had it and your parents didn't. Maybe you had it as a child and lost it. Maybe you grew up in Wales feeling on the outside of something that should have been yours. Welsh is your inheritance, and reclaiming it is one of the most quietly powerful things you can do.",
  cyReasonsTag2: 'Belonging',
  cyReasonsTitle2: 'You moved to Wales – and you mean it.',
  cyReasonsBody2: "You didn't move here to live next to Wales. You moved here to live in it. Learning the language is how you say so – to your neighbours, to the shopkeeper, to the parents at the school gate. We celebrate the people who do this. It matters more than you might realise.",
  cyReasonsTag3: 'Connecting',
  cyReasonsTitle3: "You don't live in Wales, but Wales is in you.",
  cyReasonsBody3: "A Welsh grandparent. A family name. A summer that stayed with you. A friend whose voice changes when she switches into Welsh and you want in on it. You don't have to live here to belong to the language – and the language doesn't mind a long-distance relationship.",
  cyVigKicker: "Where you'll use it",
  cyVigTitle: 'What Welsh actually unlocks.',
  cyVigLede: "The fun isn't in getting a certificate you can stick on a shelf. The fun is those moments when you can actually speak – and the whole feeling of your day gets more sparkly.",
  cyVigTag1: 'A kitchen in Caernarfon',
  cyVigTitle1: 'Talk to your nain in her language.',
  cyVigBody1: "The cup of tea, the biscuit tin, the quiet half-hour you've always had with her. Now in the language she dreams in.",
  cyVigPhrase1: "Dwi 'di bod yn dysgu Cymraeg, Nain. I ti.",
  cyVigPhraseEn1: "I've been learning Welsh, Nain. For you.",
  cyVigTag2: 'The school gate',
  cyVigTitle2: 'Stop being the parent who smiles and nods.',
  cyVigBody2: "Your kids are coming home with words you don't know. The other parents are mid-conversation when you arrive. Today, you join in.",
  cyVigPhrase2: "Sut aeth hi heddiw, 'ta?",
  cyVigPhraseEn2: 'So how did it go today, then?',
  cyVigTag3: 'A pub in Aberystwyth',
  cyVigTitle3: 'Order in Welsh. Because you can.',
  cyVigBody3: "You're not just a tourist. You're someone starting to make yourself at home, and you'll be served a bit faster than the tourists next time.",
  cyVigPhrase3: 'Peint o gwrw, plîs, a gwydrad o win gwyn.',
  cyVigPhraseEn3: 'A pint of beer, please, and a glass of white wine.',
  cyVigTag4: 'Calling from abroad',
  cyVigTitle4: 'Bring Wales with you, wherever you are.',
  cyVigBody4: "Sydney, Patagonia, Pennsylvania. The cousins back home. The friend who emigrated. Welsh travels well, and it doesn't ask where you live.",
  cyVigPhrase4: "Sut mae pawb gartre? Dwi'n eich colli chi.",
  cyVigPhraseEn4: 'How is everyone at home? I miss you.',

  // Welsh-specific Methodology Edges body (mentions mutations).
  methEdgesBody: "'Edges' are the spaces between words that you don't normally even notice. They have vital information for you, which most courses skip over entirely. In Welsh, the edges are where the mutations live – and where most learners come unstuck. We don't.",

  testiRole: 'Actor & writer (Gavin & Stacey) · SSi Welsh graduate',

  learnerSub: "You just heard the method. Here's what happened to ordinary people who stuck with it – from kitchens in Caernarfon to video calls from Patagonia, speaking Welsh with SaySomethingin.",

  outcomesFastBody: "When you've got a deadline – an Eisteddfod, a wedding, a family visit, a job interview – the only thing that gets you there is effort. Here are two ways to make that effort:",
  outcomesEasyStatLabel1: "The first Welsh words stop feeling foreign. You're building sentences out loud – clumsy, slow, but real sentences. You're saying 'Bore da' and 'Diolch' more often, and longer sentences with friends.",
  outcomesEasyStatLabel2: "You can hold a simple conversation. It's still full of gaps, you're still hunting for words, but you're talking in Welsh instead of about it. You use Welsh with your Welsh-speaking friends regularly now.",
  outcomesEasyStatLabel: "Your spoken Welsh is beginning to transform. Real conversations, in real time, with real people. You can stay in Welsh for at least 30 minutes, and you're at the beginning of your Welsh-speaking life.",

  sampleLede: 'Try three minutes – or half an hour! – of the SaySomethingin Method, right here. Welsh is teed up; you can hop into any of the others too.',

  // Welsh FAQ – different questions/answers from the templated ones.
  faqQ1: "I tried Welsh at school and it didn't stick. Will this be different?",
  faqA1: 'Yes. School Welsh is mostly about reading, writing, and getting answers right. Our course is about speaking, getting it wrong, and trying again – which is the bit your brain actually needs. Most of our learners come back to Welsh after years of "I\'m no good at languages" and end up having their first real conversation within a few months.',
  faqQ2: 'North Welsh or South Welsh – which will I learn?',
  faqA2: "Whichever you choose. We have full courses in both. Pick the one your family speaks, or the one where you live, or the one that simply sounds nicest to you. They understand each other perfectly well – it's one language, two flavours.",
  faqQ3: "I'm older / I don't have a 'language brain'. Will it still work?",
  faqA3: "Yes. Our oldest active Welsh learner is in her nineties. The method doesn't require talent – it requires showing up.",
  faqQ4: 'How much time, honestly – will five minutes a day actually get me anywhere?',
  faqA4: 'Yes, if you do it most days. Five minutes of spoken practice a day will transform your spoken Welsh in 6–12 months. Ten minutes a day gets you there faster. Cramming three hours on Sunday and nothing else works as well, if that is a better fit for you.',
  faqQ5: 'Do I actually have to say it out loud?',
  faqA5: "Yes, out loud is the course. Whispering works. Mumbling works. Silently mouthing the words works better than nothing. What doesn't work is only listening – your brain needs to try to produce the sounds for them to stick.",
  faqQ6: "What about the mutations? Everyone says they're impossible.",
  faqA6: "They're not. Mutations are 'edges' – and the method is built to teach edges. You won't sit down and learn rules; you'll hear and say them so often that the right form just sounds right. Which is how every fluent Welsh speaker does it.",
  faqQ7: "What does it cost, and what if it doesn't work for me?",
  faqA7: "It's £15 per month (or £150 per year if you'd like to save £30). And you can cancel any time.",

  cyBiggerKicker: 'Something bigger',
  cyBiggerTitle: "Start learning, and you'll be part of something bigger.",
  cyBiggerP1: "SaySomethinginWelsh started in 2009 with one goal: to help reverse language shift in Wales. Tens of thousands of learners later, that's still the goal.",
  cyBiggerP2: "When you learn Welsh with us, you become part of a wide, warm community of learners and speakers – people who get what you're doing, who'll cheer you on, and who'll meet you for a panad and a chat when you're ready. You're also helping us keep building. Every Welsh subscription supports the work we're doing for other endangered and indigenous languages around the world.",
  cyBiggerP3: "It's a quiet contribution. But over thousands of learners, quiet contributions are how a language stays alive.",

  cyClosingTitle: 'Welsh has been waiting for you.',
  cyClosingP1: "If you've read this far, the language is already in you somewhere. School might have buried it, time might have moved it sideways, distance might have held it at arm's length – but it's there.",
  cyClosingP2: "Welsh has been waiting for you. Whenever you're ready, so are we.",
  cyClosingCta1: 'Start your 7 days free',
  cyClosingCta2: 'Hear a sample first',

  cyFlowKicker: "What happens when you click 'Start your 7 days free'",
  cyFlowStep1Title: 'Start the seven-day free trial.',
  cyFlowStep1Body: 'Card details upfront, but nothing charged until day eight. The full course, no limits, the whole time.',
  cyFlowStep2Title: 'Choose your Welsh.',
  cyFlowStep2Body: 'North or South – pick the one your family speaks, the one where you live, or the one that simply sounds nicest to you.',
  cyFlowStep3Title: 'Decide on day seven.',
  cyFlowStep3Body: "Love it, and you carry on at £15 a month. Not for you, and you cancel from your account in two clicks. We'd rather have your honest 'no thanks' than your reluctant subscription.",

  footerCta: 'Cenedl heb iaith, cenedl heb galon.',
};

/* ───── per-slug config: composition, tweak defaults, sample default lang ───── */

const SLUGS = {
  spanish: {
    defaults: SPANISH_DEFAULTS,
    sampleLang: 'en-es',
    sections: ({ tweaks }) => [
      <SpanishHero key="hero" t={tweaks}/>,
      <SpanishVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-es"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  french: {
    defaults: FRENCH_DEFAULTS,
    sampleLang: 'en-fr',
    sections: ({ tweaks }) => [
      <FrenchHero key="hero" t={tweaks}/>,
      <FrenchVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-fr"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  german: {
    defaults: GERMAN_DEFAULTS,
    sampleLang: 'en-de',
    sections: ({ tweaks }) => [
      <GermanHero key="hero" t={tweaks}/>,
      <GermanVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-de"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  italian: {
    defaults: ITALIAN_DEFAULTS,
    sampleLang: 'en-it',
    sections: ({ tweaks }) => [
      <ItalianHero key="hero" t={tweaks}/>,
      <ItalianVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-it"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  portuguese: {
    defaults: PORTUGUESE_DEFAULTS,
    sampleLang: 'en-pt',
    sections: ({ tweaks }) => [
      <PortugueseHero key="hero" t={tweaks}/>,
      <PortugueseVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-pt"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  mandarin: {
    defaults: MANDARIN_DEFAULTS,
    sampleLang: 'en-cmn',
    sections: ({ tweaks }) => [
      <MandarinHero key="hero" t={tweaks}/>,
      <MandarinVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-cmn"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  japanese: {
    defaults: JAPANESE_DEFAULTS,
    sampleLang: 'en-ja',
    sections: ({ tweaks }) => [
      <JapaneseHero key="hero" t={tweaks}/>,
      <JapaneseVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <IntensiveCrossLink key="icl" variant="japanese"/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-ja"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  korean: {
    defaults: KOREAN_DEFAULTS,
    sampleLang: 'en-ko',
    sections: ({ tweaks }) => [
      <KoreanHero key="hero" t={tweaks}/>,
      <KoreanVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-ko"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  arabic: {
    defaults: ARABIC_DEFAULTS,
    sampleLang: 'en-ar',
    sections: ({ tweaks }) => [
      <ArabicHero key="hero" t={tweaks}/>,
      <ArabicVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-ar"/>,
      <FAQ key="faq" t={tweaks}/>,
    ],
  },
  welsh: {
    defaults: WELSH_DEFAULTS,
    sampleLang: 'en-cy-south',
    sections: ({ tweaks }) => [
      <WelshHero key="hero" t={tweaks}/>,
      <WelshReasons key="reasons" t={tweaks}/>,
      <WelshVignettes key="vig" t={tweaks}/>,
      <Methodology key="method" t={tweaks}/>,
      <IntensiveCrossLink key="icl" variant="welsh"/>,
      <Outcomes key="out" t={tweaks}/>,
      <TestimonialFeature key="testi" t={tweaks}/>,
      <SampleAndPricing key="sample" t={tweaks} defaultLang="en-cy-south"/>,
      <FAQ key="faq" t={tweaks}/>,
      <WelshSomethingBigger key="bigger" t={tweaks}/>,
      <WelshClosing key="closing" t={tweaks}/>,
      <WelshTrialFlow key="flow" t={tweaks}/>,
    ],
  },
};

/* ───── Tweaks panel groups (just Hero + Vignettes – the per-slug bits we
 * actually want editable. The shared Methodology/Outcomes/etc keys come
 * from SHARED_DEFAULTS and aren't edited per-page in the static HTMLs
 * either, beyond the same-everywhere defaults.) ───── */

// Two-letter Hero/Vignettes key prefix per non-Welsh slug (es/fr/de/…).
const LANG_PREFIX = {
  spanish:'es', french:'fr', german:'de', italian:'it', portuguese:'pt',
  mandarin:'zh', japanese:'ja', korean:'ko', arabic:'ar',
};

function groupsFor(slug) {
  if (slug === 'welsh') {
    return [
      { name: 'Hero', keys: ['cyHeroVariant','cyHeroKicker','cyHeroTitle','cyHeroSub1','cyHeroSub2','cyHeroCta1','cyHeroCta2','cyHeroTrust','cyHeroGiantWord','cyHeroBubble'], long:['cyHeroTitle','cyHeroSub1','cyHeroSub2','cyHeroTrust'], options:{cyHeroVariant:['A','B']} },
      { name: 'Reasons (3-up)', keys: ['cyReasonsKicker','cyReasonsTitle','cyReasonsLede','cyReasonsTag1','cyReasonsTitle1','cyReasonsBody1','cyReasonsTag2','cyReasonsTitle2','cyReasonsBody2','cyReasonsTag3','cyReasonsTitle3','cyReasonsBody3'], long:['cyReasonsLede','cyReasonsBody1','cyReasonsBody2','cyReasonsBody3'] },
      { name: "Where you'll use it", keys: ['cyVigKicker','cyVigTitle','cyVigLede','cyVigTag1','cyVigTitle1','cyVigBody1','cyVigPhrase1','cyVigPhraseEn1','cyVigTag2','cyVigTitle2','cyVigBody2','cyVigPhrase2','cyVigPhraseEn2','cyVigTag3','cyVigTitle3','cyVigBody3','cyVigPhrase3','cyVigPhraseEn3','cyVigTag4','cyVigTitle4','cyVigBody4','cyVigPhrase4','cyVigPhraseEn4'], long:['cyVigLede','cyVigBody1','cyVigBody2','cyVigBody3','cyVigBody4'] },
      { name: 'Something bigger', keys: ['cyBiggerKicker','cyBiggerTitle','cyBiggerP1','cyBiggerP2','cyBiggerP3'], long:['cyBiggerTitle','cyBiggerP1','cyBiggerP2','cyBiggerP3'] },
      { name: 'Closing CTA', keys: ['cyClosingTitle','cyClosingP1','cyClosingP2','cyClosingCta1','cyClosingCta2'], long:['cyClosingTitle','cyClosingP1','cyClosingP2'] },
      { name: 'Trial flow', keys: ['cyFlowKicker','cyFlowStep1Title','cyFlowStep1Body','cyFlowStep2Title','cyFlowStep2Body','cyFlowStep3Title','cyFlowStep3Body'], long:['cyFlowStep1Body','cyFlowStep2Body','cyFlowStep3Body'] },
    ];
  }
  // For all other languages, derive Hero/Vignettes group keys from the
  // two-letter prefix used in the per-slug TWEAK_DEFAULTS (es/fr/de/...).
  const p = LANG_PREFIX[slug];
  if (!p) return [];
  const heroKeys = [`${p}HeroVariant`,`${p}HeroKicker`,`${p}HeroTitle`,`${p}HeroSub1`,`${p}HeroSub2`,`${p}HeroCta1`,`${p}HeroCta2`,`${p}HeroTrust`,`${p}HeroGiantWord`];
  // Spanish has no Bubble key (it hardcodes ¡Hola!); the others do.
  if (p !== 'es') heroKeys.push(`${p}HeroBubble`);
  const vigKeys = [`${p}VigKicker`,`${p}VigTitle`,`${p}VigLede`];
  for (let n = 1; n <= 4; n++) {
    vigKeys.push(`${p}VigTag${n}`,`${p}VigTitle${n}`,`${p}VigBody${n}`,`${p}VigPhrase${n}`,`${p}VigPhraseEn${n}`);
  }
  return [
    { name: 'Hero', keys: heroKeys, long:[`${p}HeroTitle`,`${p}HeroSub1`,`${p}HeroSub2`,`${p}HeroTrust`], options:{[`${p}HeroVariant`]:['A','B']} },
    { name: "Where you'll use it", keys: vigKeys, long:[`${p}VigLede`,`${p}VigBody1`,`${p}VigBody2`,`${p}VigBody3`,`${p}VigBody4`] },
  ];
}

export default function LearnLang() {
  const { lang } = useParams();
  const cfg = SLUGS[lang];
  // Always call useTweaks (Rules of Hooks); fall back to {} when the
  // slug is unknown so the early-return below still works without
  // breaking hook ordering across renders.
  // Offer-test overrides. Welsh takes its bespoke per-key variant copy via
  // the 'learnWelsh' tweak block; every page also gets the 'shared' keys
  // (coach modal CTA) plus the offer-aware aside/footer/pay from shared
  // components.
  const offer = useOffer();
  let defaults = useOfferTweakDefaults(
    cfg ? cfg.defaults : {},
    ...(lang === 'welsh' ? ['learnWelsh'] : [])
  );
  // The nine non-Welsh landers share prefixed Hero components, so their
  // offer copy can't live in one tweak block the way Welsh's does. Map the
  // variant's language-neutral learnHero copy onto this slug's Hero CTA +
  // trust keys (and the shared FAQ cost answer) so every CTA point on the
  // page reflects the active offer, just like Welsh.
  const prefix = LANG_PREFIX[lang];
  if (offer && prefix && offer.config.learnHero) {
    const lh = offer.config.learnHero;
    defaults = {
      ...defaults,
      [`${prefix}HeroCta1`]:  lh.cta,
      [`${prefix}HeroTrust`]: lh.trust,
      faqA7: lh.faqA7,
    };
  }
  const { tweaks, panel } = useTweaks({
    defaults,
    groups: cfg ? groupsFor(lang) : [],
  });
  if (!cfg) return <NotFound/>;

  return (
    <div data-screen-label={`Learn ${lang}`}>
      <Header t={tweaks} hideLocale={true}/>
      <main>
        {cfg.sections({ tweaks })}
      </main>
      <Footer t={tweaks} defaultBilling="monthly"/>
      {panel}
    </div>
  );
}
